import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { Box, Stack, Typography } from "@mui/material";
import { blue, blueGrey, deepOrange, yellow } from "@mui/material/colors";
import {
  LQG_THRESHOLD_HAZARDOUS_LB,
  SQG_THRESHOLD_HAZARDOUS_LB,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";

import { MonthWasteDataItem } from "./MonthWasteDataItem";

type MonthDetailsComparisonProps = {
  vendorHazardousAmount: number;
  eManifestHazardousAmount: number;
  vendorAcutelyHazardousAmount: number;
  eManifestAcutelyHazardousAmount: number;
  vendorStateRegulatedAmount: number;
  eManifestStateRegulatedAmount: number;
  showStateRegulatedWaste: boolean;
};

export const MonthDetailsComparison = ({
  vendorHazardousAmount,
  vendorAcutelyHazardousAmount,
  vendorStateRegulatedAmount,
  eManifestHazardousAmount,
  eManifestAcutelyHazardousAmount,
  eManifestStateRegulatedAmount,
  showStateRegulatedWaste,
}: MonthDetailsComparisonProps) => {
  // Calculate if there's any vendor or e‑Manifest data across all waste classifications
  const hasAnyVendorData =
    vendorHazardousAmount > 0 || vendorAcutelyHazardousAmount > 0 || vendorStateRegulatedAmount > 0;

  const hasAnyEManifestData =
    eManifestHazardousAmount > 0 ||
    eManifestAcutelyHazardousAmount > 0 ||
    eManifestStateRegulatedAmount > 0;

  // Show missing data warning only when:
  // - This source has no data across ALL waste types AND
  // - The other source has at least some data
  const shouldShowMissingVendorData = !hasAnyVendorData && hasAnyEManifestData;
  const shouldShowMissingEManifestData = !hasAnyEManifestData && hasAnyVendorData;

  // Vendor total waste
  const vendorTotalWaste = hasAnyVendorData
    ? vendorHazardousAmount +
      vendorAcutelyHazardousAmount +
      (showStateRegulatedWaste ? vendorStateRegulatedAmount : 0)
    : null;

  // eManifest total waste
  const eManifestTotalWaste = hasAnyEManifestData
    ? eManifestHazardousAmount +
      eManifestAcutelyHazardousAmount +
      (showStateRegulatedWaste ? eManifestStateRegulatedAmount : 0)
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 1,
        columnGap: 3,
      }}
    >
      <Typography variant="body2" gutterBottom color="textPrimary">
        Vendor Data
      </Typography>
      <Typography variant="body2" gutterBottom color="textPrimary">
        e‑Manifest Data
      </Typography>

      <MonthWasteDataItem label="Total waste" amount={vendorTotalWaste}>
        {/* only show fed specific threshold comparison when state regulated is not shown */}
        {vendorTotalWaste && !showStateRegulatedWaste && (
          <Typography variant="caption" color="textSecondary" fontStyle="italic" align="right">
            {calculateFederalThresholdComparison(vendorTotalWaste)}
          </Typography>
        )}
        {shouldShowMissingVendorData && (
          <Stack direction="row" justifyContent="flex-end">
            <ErrorOutline fontSize="small" color="disabled" />
            <Typography variant="caption" color="textSecondary" fontStyle="italic" ml={0.5}>
              no vendor data
            </Typography>
          </Stack>
        )}
      </MonthWasteDataItem>
      <MonthWasteDataItem label="Total waste" amount={eManifestTotalWaste}>
        {/* only show fed specific threshold comparison when state regulated is not shown */}
        {eManifestTotalWaste && !showStateRegulatedWaste && (
          <Typography variant="caption" color="textSecondary" fontStyle="italic" align="right">
            {calculateFederalThresholdComparison(eManifestTotalWaste)}
          </Typography>
        )}
        {shouldShowMissingEManifestData && (
          <Stack direction="row" justifyContent="flex-end">
            <ErrorOutline fontSize="small" color="disabled" />
            <Typography variant="caption" color="textSecondary" fontStyle="italic" ml={0.5}>
              missing manifest data
            </Typography>
          </Stack>
        )}
      </MonthWasteDataItem>

      <MonthWasteDataItem
        label="Hazardous waste"
        amount={hasAnyVendorData ? vendorHazardousAmount : null}
        color={deepOrange[300]}
      />
      <MonthWasteDataItem
        label="Hazardous waste"
        amount={hasAnyEManifestData ? eManifestHazardousAmount : null}
        color={blueGrey[100]}
      />

      <MonthWasteDataItem
        label="Acutely hazardous waste"
        amount={hasAnyVendorData ? vendorAcutelyHazardousAmount : null}
        color={deepOrange[800]}
      />
      <MonthWasteDataItem
        label="Acutely hazardous waste"
        amount={hasAnyEManifestData ? eManifestAcutelyHazardousAmount : null}
        color={blueGrey[300]}
      />

      {showStateRegulatedWaste && (
        <>
          <MonthWasteDataItem
            label="State regulated waste"
            amount={hasAnyVendorData ? vendorStateRegulatedAmount : null}
            color={yellow[700]}
          />
          <MonthWasteDataItem
            label="State regulated waste"
            amount={hasAnyEManifestData ? eManifestStateRegulatedAmount : null}
            color={blue[200]}
          />
        </>
      )}
    </Box>
  );
};

function calculateFederalThresholdComparison(amount: number): string {
  const thresholds = {
    lqg: LQG_THRESHOLD_HAZARDOUS_LB,
    sqg: SQG_THRESHOLD_HAZARDOUS_LB,
  };

  const lqgDiff = amount - thresholds.lqg;
  const sqgDiff = amount - thresholds.sqg;

  if (amount >= thresholds.lqg) {
    return `${Math.abs(
      lqgDiff
    ).toLocaleString()} lbs above fed LQG threshold (${thresholds.lqg.toLocaleString()} lbs)`;
  } else if (amount >= thresholds.sqg) {
    return `${Math.abs(
      lqgDiff
    ).toLocaleString()} lbs below fed LQG threshold (${thresholds.lqg.toLocaleString()} lbs)`;
  } else {
    return `${Math.abs(
      sqgDiff
    ).toLocaleString()} lbs below fed SQG threshold (${thresholds.sqg.toLocaleString()} lbs)`;
  }
}
